import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2, ScanLine, Upload } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { ApiService } from '../../../services/api';
import type { PracticeExam } from '../../../types/practice-exam';
import type { ExternalSchoolOption, LgsIssueItem, LgsOpticalImportSummary } from '../../../types/external-school';
import { FilePicker } from './OpticalUploadModal';

type Slot = 'form' | 'result' | 'form2' | 'result2';
type Mode = 'internal' | 'external';

interface LgsOpticalUploadModalProps {
    exam: PracticeExam | null;
    isOpen: boolean;
    onClose: () => void;
    onImported?: () => void;
    // Kırtasiye ekranı: yalnız kurum dışı mod ve kendi okulları
    partnerMode?: boolean;
    schoolOptions?: ExternalSchoolOption[];
}

const MATCH_LABELS: Record<string, string> = { tc: 'T.C.', name: 'Ad', studentNumber: 'Öğrenci no', none: 'Eşleşmedi', external: 'Kurum dışı' };
const EMPTY_FILES: Record<Slot, File | null> = { form: null, result: null, form2: null, result2: null };

const IssueList = ({ title, tone, items, hint }: { title: string; tone: 'red' | 'amber'; items: LgsIssueItem[]; hint?: string }) => (
    <details className={`rounded-md px-3 py-2 text-xs ${tone === 'red' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
        <summary className="cursor-pointer font-semibold">{title}</summary>
        {hint && <p className="mt-1">{hint}</p>}
        <ul className="mt-1 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5">
            {items.map((item, index) => (
                <li key={index}>
                    {item.name || '(adsız)'}{item.school ? ` · ${item.school}` : ''}{item.className ? ` · ${item.className}` : ''}
                    {' · '}T.C. {item.tc || 'yok'} · No {item.studentNumber || 'yok'}
                </li>
            ))}
        </ul>
    </details>
);

/**
 * LGS optik sonuçları (form tanımı .ftm/.bldm + sonuç .txt/.dat). Kurum dışı modda öğrenciler hiçbir hesapla
 * eşleştirilmez; satırlar seçilen okula ya da optikteki kurum koduna göre okullara yazılır. Önce önizlenir.
 */
export default function LgsOpticalUploadModal({ exam, isOpen, onClose, onImported, partnerMode = false, schoolOptions }: LgsOpticalUploadModalProps) {
    const audience = exam?.audience || 'internal';
    const defaultMode: Mode = partnerMode || audience === 'external' ? 'external' : 'internal';

    const [files, setFiles] = useState<Record<Slot, File | null>>(EMPTY_FILES);
    const [secondSession, setSecondSession] = useState(false);
    const [mode, setMode] = useState<Mode>(defaultMode);
    const [schoolId, setSchoolId] = useState<string>('');
    // Okul seçili yüklemede kurum kodunu yanlış kodlayan öğrenci de seçilen okula yazılsın
    const [ignoreSchoolCode, setIgnoreSchoolCode] = useState(false);
    const [schools, setSchools] = useState<ExternalSchoolOption[]>(schoolOptions || []);
    const [preview, setPreview] = useState<LgsOpticalImportSummary | null>(null);
    const [done, setDone] = useState<LgsOpticalImportSummary | null>(null);
    const [busy, setBusy] = useState<'preview' | 'import' | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setFiles(EMPTY_FILES);
        setSecondSession(false);
        setMode(defaultMode);
        setSchoolId('');
        setIgnoreSchoolCode(false);
        setPreview(null);
        setDone(null);
        setError(null);
    }, [isOpen, exam?.id, defaultMode]);

    useEffect(() => {
        if (!isOpen || mode !== 'external') return;
        if (schoolOptions) { setSchools(schoolOptions); return; }
        ApiService.getExternalSchoolOptions()
            .then((rows) => setSchools(Array.isArray(rows) ? rows : []))
            .catch(() => setSchools([]));
    }, [isOpen, mode, schoolOptions]);

    const setFile = (slot: Slot, file: File | null) => {
        setFiles((prev) => ({ ...prev, [slot]: file }));
        setPreview(null);
        setDone(null);
    };

    const firstComplete = Boolean(files.form && files.result);
    const secondIncomplete = secondSession && Boolean(files.form2) !== Boolean(files.result2);
    const canPreview = Boolean(exam) && firstComplete && !secondIncomplete && !busy && (mode === 'internal' || schools.length > 0);

    const run = async (dryRun: boolean, ignore = ignoreSchoolCode) => {
        if (!exam) return;
        setBusy(dryRun ? 'preview' : 'import');
        setError(null);
        try {
            const fixedSchoolId = mode === 'external' ? schoolId || null : null;
            const result = await ApiService.uploadLgsOpticalResults(
                exam.id,
                secondSession ? files : { form: files.form, result: files.result },
                { dryRun, mode, schoolId: fixedSchoolId, ignoreSchoolCode: Boolean(fixedSchoolId) && ignore }
            );
            setPreview(result);
            if (!dryRun) {
                setDone(result);
                onImported?.();
            }
        } catch (err: any) {
            setError(err?.message || 'Optik dosyalar işlenemedi');
        } finally {
            setBusy(null);
        }
    };

    const matchSummary = useMemo(
        () => Object.entries(preview?.matching || {}).filter(([, count]) => Number(count) > 0),
        [preview]
    );
    const issues = preview?.issues;
    const foreign = issues?.foreignSchoolCodes || [];
    const accepted = issues?.acceptedForeignCodes || [];
    const unmapped = [...(issues?.unmappedSchoolCodes || []), ...foreign];
    const selectedSchool = schools.find((school) => school.id === schoolId);
    const codeLabel = (row: { code: string; count: number; belongsTo?: string | null }) =>
        `${row.code} kodlu ${row.count} satır${row.belongsTo ? ` (${row.belongsTo} okulunun kodu)` : ''}`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => !busy && onClose()}
            title={exam ? `${exam.name} - LGS Optik Yükle` : 'LGS Optik Yükle'}
            size="xl">
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    Optik okuyucunun <b>form tanımı</b> (.ftm / .bldm) ve <b>sonuç dosyasını</b> (.txt / .dat) yükleyin. Sözel ve sayısal
                    tek formdaysa bir çift yeter; ayrı formlarsa ikinci oturumu ekleyin ya da sonra yükleyin (öğrencinin sonucuyla birleşir).
                </p>

                {!partnerMode && audience === 'both' && (
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                        <span className="text-sm font-medium text-gray-700">Bu dosya</span>
                        <div className="inline-flex rounded-md border border-gray-300 p-0.5">
                            {([['internal', 'Kurum içi öğrencilerimizin'], ['external', 'Dış okulların']] as const).map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    disabled={Boolean(busy)}
                                    onClick={() => { setMode(value); setPreview(null); setDone(null); }}
                                    className={`rounded px-3 py-1 text-sm ${mode === value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {mode === 'external' && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                        <p className="mb-2 text-amber-900">
                            Kurum dışı öğrenciler <b>hiçbir hesapla eşleştirilmez</b>. Karnelerine okullarının bağlantısından T.C. kimlik + okul numarasıyla ulaşırlar.
                        </p>
                        {schools.length === 0 ? (
                            <p className="text-amber-800">Kayıtlı dış kurum yok. Önce "Dış Kurumlar" sekmesinden okulu ekleyin.</p>
                        ) : (
                            <label className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                                <span className="text-amber-900">Okul</span>
                                <select
                                    value={schoolId}
                                    disabled={Boolean(busy)}
                                    onChange={(e) => { setSchoolId(e.target.value); setIgnoreSchoolCode(false); setPreview(null); setDone(null); }}
                                    className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm">
                                    <option value="">Optikteki kurum koduna göre dağıt</option>
                                    {schools.map((school) => (
                                        <option key={school.id} value={school.id}>
                                            {school.name}{school.opticalCodes?.length ? ` (${school.opticalCodes.join(', ')})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {schoolId && (
                            <label className="mt-2 flex items-start gap-2 text-amber-900">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={ignoreSchoolCode}
                                    disabled={Boolean(busy)}
                                    onChange={(e) => { setIgnoreSchoolCode(e.target.checked); setPreview(null); setDone(null); }}
                                />
                                <span>
                                    Kurum kodunu yok say: dosyadaki <b>bütün satırları</b> bu okula yaz
                                    <span className="block text-xs text-amber-800">Dosya yalnız bu okula aitse ve bazı öğrenciler kurum kodunu yanlış kodladıysa işaretleyin.</span>
                                </span>
                            </label>
                        )}
                    </div>
                )}

                <div className="rounded-lg border border-gray-200 p-3">
                    <div className="mb-2 text-sm font-semibold text-gray-800">Optik</div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <FilePicker label="Form tanımı (.ftm / .fmt / .bldm)" accept=".ftm,.fmt,.bldm,.frm,.FTM,.FMT,.BLDM,.FRM" file={files.form} onChange={(file) => setFile('form', file)} disabled={Boolean(busy)} />
                        <FilePicker label="Sonuç dosyası (.txt / .dat)" accept=".txt,.TXT,.dat,.DAT" file={files.result} onChange={(file) => setFile('result', file)} disabled={Boolean(busy)} />
                    </div>
                </div>

                {secondSession ? (
                    <div className="rounded-lg border border-gray-200 p-3">
                        <div className="mb-2 flex items-center justify-between text-sm font-semibold text-gray-800">
                            <span>2. oturum</span>
                            <button type="button" className="text-xs font-normal text-gray-500 hover:underline" onClick={() => { setSecondSession(false); setFile('form2', null); setFile('result2', null); }}>
                                Kaldır
                            </button>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <FilePicker label="Form tanımı (.ftm / .fmt / .bldm)" accept=".ftm,.fmt,.bldm,.frm,.FTM,.FMT,.BLDM,.FRM" file={files.form2} onChange={(file) => setFile('form2', file)} disabled={Boolean(busy)} />
                            <FilePicker label="Sonuç dosyası (.txt / .dat)" accept=".txt,.TXT,.dat,.DAT" file={files.result2} onChange={(file) => setFile('result2', file)} disabled={Boolean(busy)} />
                        </div>
                    </div>
                ) : (
                    <button type="button" onClick={() => setSecondSession(true)} disabled={Boolean(busy)} className="text-sm text-blue-700 hover:underline">
                        + İkinci oturum ekle (sözel ve sayısal ayrı formdaysa)
                    </button>
                )}

                {secondIncomplete && (
                    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">2. oturum için form tanımı ve sonuç dosyası birlikte seçilmelidir.</div>
                )}
                {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

                {preview && (
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        {done && (
                            <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                                <CheckCircle size={16} />
                                {done.imported} öğrencinin sonucu kaydedildi{done.replaced ? ` (${done.replaced} kayıt güncellendi)` : ''}. Netler ve sıralar yenilendi.
                            </div>
                        )}

                        <div className="rounded-md border border-gray-200 bg-white p-2">
                            <table className="w-full text-xs">
                                <tbody>
                                    {(preview.sections || []).map((section) => (
                                        <tr key={section.key} className="border-t border-gray-100 first:border-t-0">
                                            <td className="py-1 pr-2 text-gray-700">{section.label}</td>
                                            <td className="py-1 pr-2 text-right text-gray-500">{section.formQuestionCount}/{section.questionCount}</td>
                                            <td className="py-1 text-right">
                                                {section.warning
                                                    ? <AlertTriangle size={14} className="inline text-amber-500" />
                                                    : <CheckCircle size={14} className="inline text-emerald-500" />}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs">
                            <span className="rounded-full bg-white px-2.5 py-1 font-medium text-gray-700 ring-1 ring-gray-200">{preview.students?.total || 0} öğrenci</span>
                            {(preview.students?.bySchool || []).map((row) => (
                                <span key={row.schoolId} className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">{row.name}: {row.count}</span>
                            ))}
                            {preview.students?.replacingExisting > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">güncellenecek {preview.students.replacingExisting}</span>}
                            {matchSummary.map(([type, count]) => (
                                <span key={type} className={`rounded-full px-2.5 py-1 ring-1 ${type === 'none' ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'}`}>
                                    {MATCH_LABELS[type] || type} {count}
                                </span>
                            ))}
                        </div>

                        {issues && issues.unreachable.count > 0 && (
                            <IssueList
                                tone="red"
                                title={`Karnesine ulaşamayacak ${issues.unreachable.count} öğrenci`}
                                hint={'Optikte T.C. ya da okul numarası boş. Kaydettikten sonra "Sonuçlar" penceresinde "Yalnız kimliği eksik olanlar" süzgeciyle bulup kalem simgesinden düzeltebilirsiniz.'}
                                items={issues.unreachable.items}
                            />
                        )}
                        {issues && mode === 'internal' && issues.missingTc.count > 0 && (
                            <IssueList tone="amber" title={`T.C. kimliği boş ${issues.missingTc.count} satır`} items={issues.missingTc.items} />
                        )}
                        {issues && issues.duplicates.count > 0 && (
                            <details className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <summary className="cursor-pointer font-semibold">Dosyada iki kez okunan {issues.duplicates.count} satır (sonuncusu alınır)</summary>
                                <ul className="mt-1 list-disc pl-5">
                                    {issues.duplicates.items.map((item, index) => <li key={index}>{item.name} · {item.session} satır {item.line}</li>)}
                                </ul>
                            </details>
                        )}
                        {unmapped.length > 0 && (
                            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <b>Yazılmayacak satırlar:</b>{' '}
                                {unmapped.map(codeLabel).join(', ')}
                                {' — '}kod hiçbir okula ait değil{schoolId ? ' ya da seçilen okulun kodu değil' : ''}. Okulun kurum kodlarını "Dış Kurumlar" sekmesinden ekleyebilirsiniz.
                                {schoolId && foreign.length > 0 && !done && (
                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            disabled={Boolean(busy)}
                                            onClick={() => { setIgnoreSchoolCode(true); run(true, true); }}
                                            className="rounded-md border border-amber-400 bg-white px-2.5 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                                            Bu satırları da {selectedSchool?.name || 'seçilen okula'} yaz
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {accepted.length > 0 && (
                            <div className={`rounded-md px-3 py-2 text-xs ${accepted.some((row) => row.belongsTo) ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'}`}>
                                <b>Kurum kodu farklı, yine de {preview.school?.name || 'seçilen okula'} yazılacak:</b>{' '}
                                {accepted.map(codeLabel).join(', ')}.
                                {accepted.some((row) => row.belongsTo) && ' Kod başka bir kayıtlı okula ait; dosyanın gerçekten bu okula ait olduğundan emin olun.'}
                            </div>
                        )}
                        {preview.warnings?.length > 0 && (
                            <ul className="list-disc space-y-0.5 rounded-md bg-amber-50 py-2 pl-6 pr-2 text-xs text-amber-800">
                                {preview.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                            </ul>
                        )}

                        <div className="max-h-64 overflow-auto rounded-md border border-gray-200 bg-white">
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-gray-100 text-gray-600">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">Öğrenci</th>
                                        {mode === 'external' && <th className="px-2 py-1.5 text-left">Okul</th>}
                                        <th className="px-2 py-1.5 text-left">Sınıf</th>
                                        <th className="px-2 py-1.5 text-left">T.C.</th>
                                        <th className="px-2 py-1.5 text-left">Okul no</th>
                                        <th className="px-2 py-1.5 text-left">Eşleşme</th>
                                        <th className="px-2 py-1.5 text-right">Cevaplanan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.preview || []).map((row, index) => (
                                        <tr key={index} className="border-t border-gray-100">
                                            <td className="px-2 py-1 text-gray-800">{row.name || '-'}</td>
                                            {mode === 'external' && <td className="px-2 py-1 text-gray-500">{row.school || '-'}</td>}
                                            <td className="px-2 py-1 text-gray-500">{row.className || '-'}</td>
                                            <td className={`px-2 py-1 ${row.tc ? 'text-gray-500' : 'text-red-600'}`}>{row.tc || 'yok'}</td>
                                            <td className={`px-2 py-1 ${row.studentNumber ? 'text-gray-500' : 'text-red-600'}`}>{row.studentNumber || 'yok'}</td>
                                            <td className={`px-2 py-1 ${row.matchType === 'none' ? 'text-amber-700' : 'text-emerald-700'}`}>{MATCH_LABELS[row.matchType] || row.matchType}</td>
                                            <td className="px-2 py-1 text-right text-gray-600">{row.answered}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {preview.students?.total > (preview.preview || []).length && (
                            <p className="text-xs text-gray-500">İlk {(preview.preview || []).length} öğrenci gösteriliyor.</p>
                        )}
                    </div>
                )}

                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                    <button
                        onClick={onClose}
                        disabled={Boolean(busy)}
                        className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50">
                        {done ? 'Kapat' : 'İptal'}
                    </button>
                    <button
                        onClick={() => run(true)}
                        disabled={!canPreview}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
                        {busy === 'preview' ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                        Önizle
                    </button>
                    <button
                        onClick={() => run(false)}
                        disabled={!canPreview || !preview || Boolean(done) || !preview.students?.total}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                        {busy === 'import' ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                        {preview?.students?.total ? `${preview.students.total} öğrenciyi kaydet` : 'Kaydet'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
